import type { AxiosRequestConfig } from 'axios';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { INTERNSHIP_APP_API } from '../Config/config';
import type {
  CombinedInternshipApplication,
  DetailedInternship,
  InternshipApplication,
  InternshipOffer,
  InternshipProcess,
  InternshipProcessWithNames,
  InternshipsApplicationsAndApplicantsOfOrganization,
  Organization,
  OrganizationRegistration,
  ReducedInternship,
  ReducedOrganization,
  Student,
  StudyLine,
  UpdateInternship,
  UpdateUserDTO,
  User,
  FinalGrade,
  InternshipWorkLogEntries,
  ExcelSummary,
  EmailNotificationFrequency,
  EmailNotificationFrequencyPreference,
  NovuSubscriberAuthSession,
  ReducedResearchAgreement,
  ResearchAgreementWithStudentName,
  OrganizationRegistrationYear,
  StudyYear,
  ReducedUniversity,
  UniversityPostDto,
  UniversityMemberInviteDto,
  ReducedStudyYear,
  ProcessStartSettings,
  CollaborationDocument,
  newCollaborationDocument,
  ReducedDepartment,
  CompetencyTable,
  CompetencyTableItem,
  EvaluationReportForComputerScience,
  EvaluationReportForMathAndMathComputerScience,
  BaseEvaluationReport,
  ConventionDocument,
  DetailedOrganizationRegistration,
  OrganizationsAndTutors,
  DepartmentsWithSupervisors,
} from '../Types/interfaces';

axiosRetry(axios, {
  retries: 3, // Number of retries
  retryDelay: (retryCount) => {
    return retryCount * 10000;
  },
  // Specify conditions to retry on, this is the default
  // The request failed due to a network error, timeout, or is idempotent and retryable.
  retryCondition: (error) =>
    axiosRetry.isNetworkOrIdempotentRequestError(error),
});

async function SendPostRequest<T, R>(
  url: string,
  data: T,
  config?: AxiosRequestConfig
): Promise<R> {
  document.body.style.cursor = 'wait';

  return await axios
    .post(url, data, config)
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      // throw the error forward
      // it should be catched after call
      throw error;
    });
}

async function SendGetRequest<T>(url: string): Promise<T> {
  document.body.style.cursor = 'wait';

  return await axios
    .get(url)
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
    });
}

async function SendGetRequestOrThrow(url: string) {
  document.body.style.cursor = 'wait';

  return await axios
    .get(url)
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      throw error;
    });
}

async function SendGetBlobRequestWithRequestBody<T>(
  url: string,
  data: Record<string, unknown> | unknown[]
): Promise<T> {
  document.body.style.cursor = 'wait';

  return await axios
    .post(url, data, {
      responseType: 'blob',
    })
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      throw error;
    });
}

async function SendGetBlobRequest<T>(url: string): Promise<T> {
  document.body.style.cursor = 'wait';

  return await axios
    .get(url, { responseType: 'blob' })
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      throw error;
    });
}

async function SendPostBlobRequest<T>(url: string): Promise<T> {
  document.body.style.cursor = 'wait';

  return await axios
    .post(url, null, { responseType: 'blob' })
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      throw error;
    });
}

async function SendPatchRequest<T, R>(url: string, data: T): Promise<R> {
  document.body.style.cursor = 'wait';

  return await axios
    .patch(url, data, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      throw error;
    });
}

async function SendPutRequestOrThrow(url: string, data: any) {
  document.body.style.cursor = 'wait';

  return await axios
    .put(url, data)
    .then((response) => {
      document.body.style.cursor = 'default';
      return response.data;
    })
    .catch(function (error) {
      document.body.style.cursor = 'default';
      console.log(error.toJSON());
      throw error;
    });
}

async function SendDeleteRequest<T>(url: string): Promise<T> {
  return await axios
    .delete(url)
    .then((response) => {
      return response.data;
    })
    .catch(function (error) {
      console.log(error.toJSON());
      throw error;
    });
}

export async function GetUsers(): Promise<User[]> {
  const url = `${INTERNSHIP_APP_API}/users/`;
  return await SendGetRequest(url);
}

export async function GetUser(userId: string): Promise<User> {
  const url = `${INTERNSHIP_APP_API}/users/${userId}`;
  return await SendGetRequest(url);
}

export async function GetUserByEmail(email: string): Promise<User> {
  // convert email safe format ex: test@email.com -> test%40email.com
  const safeEmail = encodeURIComponent(email);
  const url = `${INTERNSHIP_APP_API}/users/by-email?email=${safeEmail}`;
  return await SendGetRequest(url);
}

export async function GetNovuSubscriberAuthSession() {
  const url = `${INTERNSHIP_APP_API}/users/notification-subscriber-auth`;
  return (await SendGetRequestOrThrow(url)) as NovuSubscriberAuthSession;
}

export async function GetNotificationEmailFrequency() {
  const url = `${INTERNSHIP_APP_API}/users/notification-email-frequency`;
  return (await SendGetRequestOrThrow(
    url
  )) as EmailNotificationFrequencyPreference;
}

export async function UpdateNotificationEmailFrequency(
  frequency: EmailNotificationFrequency
) {
  const url = `${INTERNSHIP_APP_API}/users/notification-email-frequency`;
  return (await SendPutRequestOrThrow(url, {
    frequency,
  })) as EmailNotificationFrequencyPreference;
}

export async function LoginUser(
  lastName: string,
  firstName: string,
  email: string
): Promise<User> {
  const data = {
    lastName,
    firstName,
    email,
  };
  const url = `${INTERNSHIP_APP_API}/users/login`;
  console.log('BEFORE LOGIN: ', data);
  return await SendPostRequest(url, data);
}

export async function GetUserById(representativeId: string): Promise<User> {
  const url = `${INTERNSHIP_APP_API}/users/${representativeId}`;
  return (await SendGetRequest(url)) as User;
}

export async function AddRoleToUser(
  userId: string,
  role: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/users/${userId}/roles`;
  return await SendPostRequest(url, { roles: role });
}

export async function GetInternshipProcessesByUserId(
  userId: string
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/tutors/${userId}/internship-processes`;
  return (await SendGetRequest(url)) as InternshipProcessWithNames[];
}

export async function GetInternshipProcessesById(
  id: string
): Promise<InternshipProcess> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${id}`;
  return (await SendGetRequest(url)) as InternshipProcess;
}

export async function GetInternshipApplicationsById(
  applicationId: number
): Promise<CombinedInternshipApplication> {
  const url = `${INTERNSHIP_APP_API}/internship-applications/${applicationId}`;
  return await SendGetRequest(url);
}

export async function AcceptInternshipApplication(
  organizationId: number,
  offerId: number,
  applicationId: number
): Promise<InternshipProcess> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-offers/${offerId}/applications/${applicationId}/accept`;
  return await SendPostRequest(url, null);
}

export async function RejectInternshipApplication(
  organizationId: number,
  offerId: number,
  applicationId: number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-offers/${offerId}/applications/${applicationId}/reject`;
  return await SendPostRequest(url, null);
}

export async function GetInternshipOffersOfOrganization(
  organizationId: string
): Promise<ReducedInternship[]> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-offers`;
  return await SendGetRequest(url);
}

export async function GetInternshipOfferById(
  internshipId: string
): Promise<DetailedInternship> {
  const url = `${INTERNSHIP_APP_API}/internship-offers/${internshipId}`;
  return (await SendGetRequest(url)) as DetailedInternship;
}

export async function GetInternshipOffers(): Promise<ReducedInternship[]> {
  const url = `${INTERNSHIP_APP_API}/internship-offers`;
  return await SendGetRequest(url);
}

export async function GetActiveInternshipOffers(): Promise<
  ReducedInternship[]
> {
  const url = `${INTERNSHIP_APP_API}/internship-offers/active`;
  return await SendGetRequest(url);
}

export async function UpdateInternshipOffer(
  internshipId: string,
  internshipOffer: UpdateInternship
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/internship-offers/${internshipId}`;
  return await SendPatchRequest(url, internshipOffer);
}

export async function PostInternshipOffer(
  internshipOffer: InternshipOffer
): Promise<DetailedInternship> {
  const url = `${INTERNSHIP_APP_API}/internship-offers`;
  return await SendPostRequest(url, internshipOffer);
}
export async function GetInternshipOfferApplications(
  organizationId: string
): Promise<CombinedInternshipApplication[]> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-offers/applications`;
  return await SendGetRequest(url);
}

export async function GetStudentById(studentId: string): Promise<Student> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}`;
  return await SendGetRequest(url);
}

export async function GetUserOrganizations(
  userId: string
): Promise<ReducedOrganization[]> {
  const url = `${INTERNSHIP_APP_API}/users/${userId}/organizations`;
  return await SendGetRequest(url);
}

export async function GetOrganizations(): Promise<ReducedOrganization[]> {
  const url = `${INTERNSHIP_APP_API}/organizations`;
  return await SendGetRequest(url);
}

export async function GetOrganizationById(
  organizationId: string
): Promise<Organization> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}`;
  return await SendGetRequest(url);
}

export async function GetRegistrationYearOfOrganization(
  organizationId: string
) {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/years`;
  return (await SendGetRequest(url)) as OrganizationRegistrationYear;
}

export async function PostOrganization(organization: Organization) {
  const url = `${INTERNSHIP_APP_API}/organizations`;
  return await SendPostRequest(url, organization);
}

export async function GetTutorsOfOrganization(
  organizationId: string
): Promise<User[]> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/tutors`;
  return await SendGetRequest(url);
}

export async function GetOrganizationsAndTutorsOfCompanyOwner(
  organizationOwnerId: string | number
): Promise<Array<OrganizationsAndTutors>> {
  const url = `${INTERNSHIP_APP_API}/users/${organizationOwnerId}/organizations-tutors`;
  return await SendGetRequest(url);
}

export async function AddTutorToOrganization(
  organizationId: string,
  userId: string
): Promise<Organization> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/tutors/${userId}`;
  return await SendPostRequest(url, null);
}

export async function UpdateOrganizationRegistration(
  registrationId: string | number,
  data?: { registrationNumber: string; registrationDate: string }
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/organization-registrations/${registrationId}`;
  return await SendPatchRequest(url, data ?? null);
}

export async function GetOrganizationRegistrationsByOrganizationId(
  organizationId: string
): Promise<Array<OrganizationRegistration>> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/organization-registrations`;
  return (await SendGetRequest(url)) as Array<OrganizationRegistration>;
}

export async function GetOrganizationRegistrationOfOrganizationByStudyYear(
  organizationId: string,
  studyYearId: string
) {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/organization-registrations/${studyYearId}`;
  return (await SendGetRequest(url)) as OrganizationRegistration;
}

export async function GetCollaborationDocumentOfOrganizationRegistration(
  registrationId: string
) {
  const url = `${INTERNSHIP_APP_API}/organization-registrations/${registrationId}/collaborationDocuments`;
  return (await SendGetRequest(url)) as CollaborationDocument;
}

export async function GetOrganizationRegistrations(): Promise<
  Array<OrganizationRegistration>
> {
  const url = `${INTERNSHIP_APP_API}/organization-registrations`;
  return await SendGetRequest(url);
}

export async function GetDetailedOrganizationRegistrations(): Promise<
  Array<DetailedOrganizationRegistration>
> {
  const url = `${INTERNSHIP_APP_API}/organization-registrations/detailed`;
  return await SendGetRequest(url);
}

export async function SignRegistrationByOrganization(
  registrationId: string | number,
  organizationId: string | number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/organization-registrations/${registrationId}`;
  return await SendPatchRequest(url, null);
}

export async function SignRegistrationByLegalDepartment(
  registrationId: string
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/university-departments/1/organization-registrations/${registrationId}`;
  return await SendPatchRequest(url, null);
}

export async function SignRegistrationByRector(
  registrationId: string
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/universities/1/organization-registrations/${registrationId}/rector-sign`;
  return await SendPatchRequest(url, null);
}

export async function GetStudentsAppliedToInternshipOffer(
  organizationId: string,
  offerId: string
): Promise<User[]> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-offers/${offerId}/students`;
  return await SendGetRequest(url);
}

export async function RemoveIntershipApplication(
  userId: string,
  internshipId: string
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${userId}/eduprograms/1/sessions/1/internships/${internshipId}`;
  return await SendDeleteRequest(url);
}

export async function ApplyToInternship(
  userId: string,
  internshipId: string,
  sessionId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${userId}/eduprograms/1/sessions/${sessionId}/internships/${internshipId}`;
  return await SendPostRequest(url, null);
}

export async function GetTeachingStaffOfUniversityDepartment(
  departmentId: string
): Promise<User[]> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${departmentId}/teaching-staff`;
  return await SendGetRequest(url);
}

export async function GetInternshipProcessesByUniversityDepartment(
  departmentId: string
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${departmentId}/internship-processes?hasTutor=true&hasSupervisor=false`;
  return await SendGetRequest(url);
}

export async function AssignStudentsToSupervisor(
  departmentId: string,
  supervisorId: string,
  students: number[]
): Promise<number[]> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${departmentId}/supervisors/${supervisorId}/internship-processes`;
  return await SendPostRequest(url, students);
}

export async function AssignStudentsToTutor(
  organizationId: string,
  offerId: string,
  tutorId: string,
  students: number[]
): Promise<unknown> {
  console.log(students);
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-offers/${offerId}/tutors/${tutorId}/students`;
  return await SendPostRequest(url, students);
}

export async function GetUniversityDepartmentsOfDepartmentRepresentative(
  representativeId: string
): Promise<{ name: string; id: number }[]> {
  const url = `${INTERNSHIP_APP_API}/department-representative/${representativeId}/university-departments`;
  return await SendGetRequest(url);
}

export async function GetUniversityDepartmentsOfSupervisor(
  supervisorId: string
): Promise<{ name: string; id: number }[]> {
  const url = `${INTERNSHIP_APP_API}/supervisors/${supervisorId}/departments`;
  return await SendGetRequest(url);
}

export async function SkipPaperwork(processId: number): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/skip-paperwork`;
  return await SendPostRequest(url, null);
}

export async function GetInternshipById(
  internshipId: number
): Promise<InternshipProcess> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${internshipId}`;
  return await SendGetRequest(url);
}

export async function UpdateUser(
  userId: number,
  userDTO: UpdateUserDTO
): Promise<User> {
  const url = `${INTERNSHIP_APP_API}/users/${userId}`;
  return await SendPatchRequest(url, userDTO);
}

export async function AddTutorsToOrganizationByEmail(
  organizationId: number,
  tutorEmails: string[]
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/tutors`;
  return await SendPostRequest(url, tutorEmails);
}

export async function GetPendingInternshipApplicants(
  organizationId: number
): Promise<User[]> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/applicants`;
  return await SendGetRequest(url);
}

export async function GetInternshipsApplicationsAndApplicantsOfOrganization(
  organizationId: number
): Promise<InternshipsApplicationsAndApplicantsOfOrganization> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internshipoffers-applications-applicants`;
  return await SendGetRequest(url);
}

export async function GetStudyLines(): Promise<StudyLine[]> {
  const url = `${INTERNSHIP_APP_API}/study-lines`;
  return await SendGetRequest(url);
}

export async function GetStudyLineById(
  studyLineId: number
): Promise<StudyLine> {
  const url = `${INTERNSHIP_APP_API}/study-lines/${studyLineId}`;
  return await SendGetRequest(url);
}

export async function UpdateStudent(
  studentId: number,
  updateDTO: Partial<Student>
): Promise<Student> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}`;
  return await SendPatchRequest(url, updateDTO);
}

export async function GetStudentApplications(
  studentId: number
): Promise<InternshipApplication[]> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/applications`;
  return await SendGetRequest(url);
}

export async function WorkSafetyInstructionReading(
  processId: number,
  studentId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes/${processId}/work-safety`;
  return await SendPostRequest(url, null);
}

export async function RouteStatementFilling(
  processId: number,
  studentId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes/${processId}/route-statement`;
  return await SendPostRequest(url, null);
}

export async function ActivityDeclarationFillingFromUniversity(
  processId: number,
  studentId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes/${processId}/activity-declaration-ubb`;
  return await SendPostRequest(url, null);
}

export async function ActivityDeclarationFillingFromOrganization(
  processId: number,
  studentId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes/${processId}/activity-declaration-organization`;
  return await SendPostRequest(url, null);
}

export async function AddEvaluationReport(
  processId: number,
  evaluationReport:
    | EvaluationReportForComputerScience
    | EvaluationReportForMathAndMathComputerScience
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/evaluation-reports`;
  return await SendPostRequest(url, evaluationReport);
}

export async function GetEvaluationReportById(
  reportId: number
): Promise<BaseEvaluationReport> {
  const url = `${INTERNSHIP_APP_API}/evaluation-reports/${reportId}`;
  return await SendGetRequest(url);
}

export async function GradeEvaluationReport(
  processId: number,
  grade: FinalGrade
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/grade`;
  return await SendPatchRequest(url, grade);
}

export async function ReGradeEvaluationReport(
  processId: number,
  grade: FinalGrade
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/regrade`;
  return await SendPatchRequest(url, grade);
}

//Change the url when it's used by a Work Safety Member
export async function ApproveLabourProtection(
  processId: number,
  ind: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${ind}/internship-processes/${processId}/labour-protections`;
  return await SendPostRequest(url, null);
}

export async function GenerateInternshipConventionDocument(
  processId: number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/convention/regenerate`;
  return await SendPostRequest(url, null);
}

export async function SignConventionByStudent(
  processId: number,
  studentId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes/${processId}/conventions`;
  return await SendPostRequest(url, null);
}

export async function SignConventionByOrganization(
  processId: number,
  organizationId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-processes/${processId}/sign`;
  return await SendPostRequest(url, null);
}

export async function SignConventionByTutor(
  organizationId: number,
  processId: number,
  tutorId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/internship-processes/${processId}/tutors/${tutorId}/sign`;
  return await SendPostRequest(url, null);
}

export async function SignConventionBySupervisor(
  processId: number,
  supervisorId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${0}/internship-processes/${processId}/university-supervisors/${supervisorId}/sign`;
  return await SendPostRequest(url, null);
}

export async function SignConventionByRector(
  processId: number,
  rectorId: number
): Promise<unknown> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${0}/internship-processes/${processId}/rectors/${rectorId}/sign`;
  return await SendPostRequest(url, null);
}

export async function GetWorkLogEntries(
  WorkLogId: number
): Promise<InternshipWorkLogEntries[]> {
  const url = `${INTERNSHIP_APP_API}/worklogs/${WorkLogId}/entries`;
  return await SendGetRequest(url);
}

export async function GetInternshipProcessesByOrganizations(
  organizationIds: number[]
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/organizations-internship-processes`;
  return await SendPostRequest(url, organizationIds);
}

export async function GetInternshipProcessesOfTutor(
  tutorId: number
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/tutors/${tutorId}/internship-processes`;
  return (await SendGetRequest(url)) as InternshipProcessWithNames[];
}

export async function GetResearchAgreementsOfTutor(
  tutorId: number
): Promise<ResearchAgreementWithStudentName[]> {
  const url = `${INTERNSHIP_APP_API}/tutors/${tutorId}/research-agreements`;
  return (await SendGetRequest(url)) as ResearchAgreementWithStudentName[];
}

export async function GetResearchAgreementsOfCoordonator(
  coordonatorId: number
): Promise<ResearchAgreementWithStudentName[]> {
  const url = `${INTERNSHIP_APP_API}/coordinators/${coordonatorId}/research-agreements`;
  return await SendGetRequest(url);
}

export async function GetInternshipProcessesOfUniversitySupervisor(
  supervisorId: number
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/university-supervisors/${supervisorId}/internship-processes`;
  return (await SendGetRequest(url)) as InternshipProcessWithNames[];
}

export async function GetInternshipProcessesForRector(
  rectorId: number
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/rectors/${rectorId}/internship-processes`;
  return (await SendGetRequest(url)) as InternshipProcessWithNames[];
}

export async function ExportPackageByProcessId(
  processIds: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/export-package`;
  return await SendGetBlobRequestWithRequestBody(url, {
    processIds: processIds,
  });
}

export async function ExportCollaborationPackage(): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/export-package/collaborations`;
  return await SendPostBlobRequest(url);
}

export async function GetSupervisorsNotInDepartment(
  departmentId: number
): Promise<User[]> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${departmentId}/not-supervisors`;
  return (await SendGetRequest(url)) as User[];
}

export async function AddSupervisorsToDepartment(
  departmentId: number,
  supervisorIds: number[]
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${departmentId}/add-supervisors`;
  return await SendPostRequest(url, supervisorIds);
}

export async function InviteSupervisorsToDepartment(
  departmentId: number,
  supervisorEmails: string[]
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/university-departments/${departmentId}/invite-supervisors`;
  return await SendPostRequest(url, supervisorEmails);
}

// GetInternshipProcessesToSign

export async function GetInternshipProcessesToSign(
  userId: number
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/${userId}/internship-processes-to-sign`;
  return await SendGetBlobRequest(url);
}

export async function GetFilteredInternshipProcessesToSignForOrganization(
  userId: number,
  selectedProcessIds: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/${userId}/internship-processes-to-sign/filtered/organization`;
  return await SendGetBlobRequestWithRequestBody(url, selectedProcessIds);
}

export async function GetFilteredInternshipProcessesToSignForTutor(
  userId: number,
  selectedProcessIds: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/${userId}/internship-processes-to-sign/filtered/tutor`;
  return await SendGetBlobRequestWithRequestBody(url, selectedProcessIds);
}

export async function GetFilteredInternshipProcessesToSignForRector(
  userId: number,
  selectedProcessIds: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/${userId}/internship-processes-to-sign/filtered/rector`;
  return await SendGetBlobRequestWithRequestBody(url, selectedProcessIds);
}

export async function GetFilteredInternshipProcessesToSignForSupervisor(
  userId: number,
  selectedProcessIds: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/${userId}/internship-processes-to-sign/filtered/supervisor`;
  return await SendGetBlobRequestWithRequestBody(url, selectedProcessIds);
}

export async function GetSummaryOfInternshipProcessesOfUniversitySupervisor(
  supervisorId: number
): Promise<ExcelSummary[]> {
  const url = `${INTERNSHIP_APP_API}/university-supervisors/${supervisorId}/internship-processes/export-summary`;
  return (await SendGetRequest(url)) as ExcelSummary[];
}

export async function GetInternshipProcessesOfStudent(
  studentId: number
): Promise<InternshipProcessWithNames[]> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes`;
  return (await SendGetRequest(url)) as InternshipProcessWithNames[];
}

export async function UpdateResearchAgreement(
  processId: number,
  data: ReducedResearchAgreement
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/research-agreement`;
  return await SendPatchRequest(url, data);
}

export async function GetResearchAgreementById(
  studentId: number
): Promise<ResearchAgreementWithStudentName[]> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/research-agreements`;
  return await SendGetRequest(url);
}

export async function SignResearchAgreementByStudent(
  studentId: number,
  processId: number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/students/${studentId}/internship-processes/${processId}/research-agreement/sign`;
  return await SendPatchRequest(url, null);
}

export async function SignResearchAgreementByTutor(
  tutorId: number,
  processId: number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/tutors/${tutorId}/internship-processes/${processId}/research-agreement/sign`;
  return await SendPatchRequest(url, null);
}

export async function SignResearchAgreementByUniversityCoordinator(
  coordinatorId: number,
  processId: number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/coordinators/${coordinatorId}/internship-processes/${processId}/research-agreement/sign`;
  return await SendPatchRequest(url, null);
}

export async function RejectResearchAgreementByTutor(
  tutorId: number,
  processId: number
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/tutors/${tutorId}/internship-processes/${processId}/research-agreement/reject`;
  return await SendPatchRequest(url, null);
}

export async function RegisterCollaborationDocument(
  processId: number,
  data: { registrationNumber: number; registrationDate: string }
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/organization-registrations/${processId}/register`;
  return await SendPatchRequest(url, data);
}

export async function RegisterConventionDocument(
  processId: number,
  data: { registrationNumber: number; registrationDate: string }
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/convention/register`;
  return await SendPatchRequest(url, data);
}

export async function GetPendingConventionRegistrations(): Promise<
  InternshipProcessWithNames[]
> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/convention/pending`;
  return (await SendGetRequest(url)) as InternshipProcessWithNames[];
}

export async function GetAllRegisteredConventionDocuments(): Promise<
  ConventionDocument[]
> {
  const url = `${INTERNSHIP_APP_API}/conventions/registered`;
  return await SendGetRequest(url);
}

export async function GetCurrentStudyYear(): Promise<StudyYear> {
  const url = `${INTERNSHIP_APP_API}/study-years/current`;
  return (await SendGetRequest(url)) as StudyYear;
}

export async function GetUniversities(): Promise<Array<ReducedUniversity>> {
  const url = `${INTERNSHIP_APP_API}/universities`;
  return (await SendGetRequest(url)) as ReducedUniversity[];
}

export async function GetStudyYears(): Promise<Array<StudyYear>> {
  const url = `${INTERNSHIP_APP_API}/study-years`;
  return (await SendGetRequest(url)) as Array<StudyYear>;
}

export async function GetStudyYearById(
  studyYearId: string
): Promise<StudyYear> {
  const url = `${INTERNSHIP_APP_API}/study-years/${studyYearId}`;
  return (await SendGetRequest(url)) as StudyYear;
}

export async function GetStudyYearOfOrganization(
  organizationId: string
): Promise<StudyYear> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/study-years`;
  return (await SendGetRequest(url)) as StudyYear;
}

export async function UpdateCurrentStudyYear(
  newStudyYear: ReducedStudyYear
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/study-years`;
  await SendPostRequest(url, newStudyYear);
}

export async function ArchiveAnnualCollaborationDocuments(): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/collaboration-documents`;
  await SendPatchRequest(url, null);
}

export async function GetCurrentCollaborationDocumentOfOrganization(
  organizationId: string
): Promise<CollaborationDocument> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/collaboration-documents/current`;
  return (await SendGetRequest(url)) as CollaborationDocument;
}

export async function UpdateCollaborationDocument(
  organizationId: string,
  newCollaborationDocument: newCollaborationDocument
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/collaboration-documents`;
  await SendPatchRequest(url, newCollaborationDocument);
}

export async function GetCollaborationDocumentsOfOrganization(
  organizationId: string
): Promise<Array<CollaborationDocument>> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/collaboration-documents`;
  return (await SendGetRequest(url)) as Array<CollaborationDocument>;
}

export async function PostUniversity(
  university: UniversityPostDto
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/universities`;
  await SendPostRequest(url, university);
}

export async function InviteMembersToUniversity(
  universityId: string,
  universityMemberInviteDto: UniversityMemberInviteDto
): Promise<void> {
  const url = `${INTERNSHIP_APP_API}/universities/${universityId}/members`;
  await SendPostRequest(url, universityMemberInviteDto);
}

export async function GetDepartmentsOfUniversity(
  universityId: string
): Promise<Array<ReducedDepartment>> {
  const url = `${INTERNSHIP_APP_API}/universities/${universityId}/departments`;
  return (await SendGetRequest(url)) as Array<ReducedDepartment>;
}

export async function GetCompetencyTableById(
  competenctyTableId: string
): Promise<CompetencyTable> {
  const url = `${INTERNSHIP_APP_API}/competency-tables/${competenctyTableId}`;
  return await SendGetRequest(url);
}

export async function GetCompetencyTableByProcessId(
  processId: string | number
): Promise<CompetencyTable> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/competency-tables`;
  return await SendGetRequest(url);
}

export async function AddCompetencytTable(
  processId: string | number,
  postCompetencyTableDto: CompetencyTable
): Promise<CompetencyTable> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/competencty-tables`;
  return await SendPostRequest(url, postCompetencyTableDto);
}

export async function UpdateCompetencyTable(
  competencyTableId: string | number,
  updateCompetencyTableDto: CompetencyTable
): Promise<CompetencyTable> {
  const url = `${INTERNSHIP_APP_API}/competency-tables/${competencyTableId}`;
  return await SendPatchRequest(url, updateCompetencyTableDto);
}

export async function AddRowToCompetencyTable(
  competencyTableId: string | number,
  postCompetencyTableItemsDto: CompetencyTableItem
): Promise<CompetencyTableItem> {
  const url = `${INTERNSHIP_APP_API}/competency-tables/${competencyTableId}/rows`;
  return await SendPostRequest(url, postCompetencyTableItemsDto);
}

export async function UpdateRowCompetencyTable(
  competencyTableId: string | number,
  rowId: string | number,
  updateCompetencyTableItemsDto: CompetencyTableItem
): Promise<CompetencyTableItem> {
  const url = `${INTERNSHIP_APP_API}/competency-tables/${competencyTableId}/rows/${rowId}`;
  return await SendPatchRequest(url, updateCompetencyTableItemsDto);
}

export async function DeleteRowCompetencyTable(
  competencyTableId: string | number,
  rowId: string | number
): Promise<boolean> {
  const url = `${INTERNSHIP_APP_API}/competency-tables/${competencyTableId}/rows/${rowId}`;
  return await SendDeleteRequest(url);
}

export async function GetCurrentOrganizationRegistrationByOrganizationId(
  organizationId: string
): Promise<OrganizationRegistration> {
  const url = `${INTERNSHIP_APP_API}/organizations/${organizationId}/organization-registrations/current`;
  return (await SendGetRequest(url)) as OrganizationRegistration;
}

export async function GetInternshipProcessesOfOrganizationRepresentative(
  representativeId: string
): Promise<Array<InternshipProcessWithNames>> {
  const url = `${INTERNSHIP_APP_API}/organization-representatives/${representativeId}/internship-processes`;
  return (await SendGetRequest(url)) as Array<InternshipProcessWithNames>;
}

export async function DownloadRegisteredConventionsZip(
  ids: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/conventions/registered/zip`;
  return await SendPostRequest<number[], Blob>(url, ids, {
    responseType: 'blob',
  });
}

export async function GetDepartmentsWithSupervisors(): Promise<
  Array<DepartmentsWithSupervisors>
> {
  const url = `${INTERNSHIP_APP_API}/departments/with-supervisors`;
  return await SendGetRequest(url);
}

export async function GetOrganizationRegistrationByIdPdfUrl(
  registrationId: string | number
): Promise<string> {
  const url = `${INTERNSHIP_APP_API}/organization-registrations/${registrationId}/pdf-url`;
  return await SendGetRequest(url);
}

export async function DownloadCollaborationDocumentsZip(
  registrationIds: number[]
): Promise<Blob> {
  const url = `${INTERNSHIP_APP_API}/organization-registrations/collaboration-documents/download-zip`;
  return await SendPostRequest<number[], Blob>(url, registrationIds, {
    responseType: 'blob',
  });
}

export async function GetOrganizationProcessStatusName(
  processId: number
): Promise<string> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/organization-process/status`;
  return await SendGetRequest(url);
}

export async function GetUniversityProcessStatusName(
  processId: number
): Promise<string> {
  const url = `${INTERNSHIP_APP_API}/internship-processes/${processId}/university-process/status`;
  return await SendGetRequest(url);
}

export async function GetProcessStartSettings(): Promise<ProcessStartSettings> {
  const url = `${INTERNSHIP_APP_API}/process-start-settings`;
  return (await SendGetRequestOrThrow(url)) as ProcessStartSettings;
}

export async function UpdateProcessStartSettings(
  settings: Partial<ProcessStartSettings>
): Promise<ProcessStartSettings> {
  const url = `${INTERNSHIP_APP_API}/process-start-settings`;
  return await SendPatchRequest<
    Partial<ProcessStartSettings>,
    ProcessStartSettings
  >(url, settings);
}
